import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  LayoutGrid, Plus, Image as ImageIcon, FileText, Link as LinkIcon,
  Trash2, Save, Loader2, X, Type, MousePointer2, Move, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_COLORS = ['#f97316', '#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b'];

export default function CanvasPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const targetBoardId = searchParams.get('board');
  const headers = { Authorization: `Bearer ${token}` };
  const canvasRef = useRef(null);

  const [boards, setBoards] = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItemType, setNewItemType] = useState('image');
  const [newItemContent, setNewItemContent] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');

  const [drag, setDrag] = useState(null); // {id, offsetX, offsetY}

  const loadBoards = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/canvas/boards`, { headers });
      setBoards(data || []);
      if (data?.length) {
        if (targetBoardId) {
          const target = data.find(b => b.id === targetBoardId);
          if (target) { setActiveBoard(target); }
          else if (!activeBoard) setActiveBoard(data[0]);
        } else if (!activeBoard) {
          setActiveBoard(data[0]);
        }
      }
    } catch {
      // user may have none
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, targetBoardId]);

  const createBoard = async () => {
    const name = newBoardName.trim() || 'Untitled Board';
    try {
      const { data } = await axios.post(`${API}/canvas/boards`, { name, description: '' }, { headers });
      setBoards([data, ...boards]);
      setActiveBoard(data);
      setNewBoardOpen(false);
      setNewBoardName('');
      toast.success('Board created');
    } catch (e) {
      toast.error('Failed to create board');
    }
  };

  const deleteBoard = async (boardId) => {
    if (!window.confirm('Delete this board?')) return;
    try {
      await axios.delete(`${API}/canvas/boards/${boardId}`, { headers });
      const remaining = boards.filter(b => b.id !== boardId);
      setBoards(remaining);
      setActiveBoard(remaining[0] || null);
      toast.success('Board deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const saveBoard = async () => {
    if (!activeBoard) return;
    setSaving(true);
    try {
      await axios.put(`${API}/canvas/boards/${activeBoard.id}`, {
        name: activeBoard.name,
        items: activeBoard.items || [],
      }, { headers });
      toast.success('Board saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const shareBoard = async () => {
    if (!activeBoard) return;
    try {
      setSharing(true);
      toast.loading('Creating public link...', { id: 'canvas-share' });
      const { data } = await axios.post(`${API}/shares`, {
        resource_type: 'canvas',
        resource_id: activeBoard.id,
      }, { headers });
      const publicUrl = `${window.location.origin}${data.share_url || data.path}`;
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success('Public link copied to clipboard', { id: 'canvas-share' });
      } catch {
        toast.success(`Link created: ${publicUrl}`, { id: 'canvas-share', duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link', { id: 'canvas-share' });
    } finally {
      setSharing(false);
    }
  };

  const addItem = () => {
    if (newItemType !== 'note' && !newItemContent.trim()) {
      return toast.error('Add content first');
    }
    const item = {
      id: crypto.randomUUID(),
      type: newItemType,
      content: newItemType === 'note' ? newItemLabel : newItemContent,
      label: newItemLabel,
      x: 60 + Math.random() * 200,
      y: 60 + Math.random() * 200,
      width: newItemType === 'note' ? 200 : 240,
      height: newItemType === 'note' ? 140 : 240,
      color: ITEM_COLORS[Math.floor(Math.random() * ITEM_COLORS.length)],
      metadata: {},
    };
    setActiveBoard({ ...activeBoard, items: [...(activeBoard.items || []), item] });
    setAddItemOpen(false);
    setNewItemContent('');
    setNewItemLabel('');
  };

  const removeItem = (itemId) => {
    setActiveBoard({ ...activeBoard, items: activeBoard.items.filter(i => i.id !== itemId) });
  };

  const startDrag = (e, item) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    setDrag({ id: item.id, offsetX: mouseX - item.x, offsetY: mouseY - item.y });
  };

  const onMove = (e) => {
    if (!drag) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - drag.offsetX);
    const y = Math.max(0, e.clientY - rect.top - drag.offsetY);
    setActiveBoard(b => ({
      ...b,
      items: b.items.map(it => it.id === drag.id ? { ...it, x, y } : it),
    }));
  };

  const endDrag = () => setDrag(null);

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="canvas-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <LayoutGrid className="w-5 h-5 text-orange-400 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="canvas-title">Canvas</h1>
                <p className="text-sm text-white/40">Moodboards & visual workflow chaining</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={activeBoard?.id || ''} onChange={(e) => setActiveBoard(boards.find(b => b.id === e.target.value))}
                data-testid="canvas-board-select"
                className="bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white max-w-[200px]">
                <option value="">— Select board —</option>
                {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <Button variant="outline" onClick={() => setNewBoardOpen(true)} data-testid="canvas-new-board"
                className="border-white/10 text-white/80 hover:bg-white/5">
                <Plus className="w-4 h-4 mr-1" />New
              </Button>
              {activeBoard && (
                <>
                  <Button onClick={() => setAddItemOpen(true)} data-testid="canvas-add-item"
                    className="bg-white/[0.08] hover:bg-white/[0.12] text-white border-0">
                    <Plus className="w-4 h-4 mr-1" />Add Item
                  </Button>
                  <Button onClick={shareBoard} disabled={sharing} data-testid="canvas-share"
                    className="bg-white/[0.08] hover:bg-white/[0.12] text-white border-0">
                    {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-1" />Share</>}
                  </Button>
                  <Button onClick={saveBoard} disabled={saving} data-testid="canvas-save"
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Canvas area */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {loading ? (
            <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>
          ) : !activeBoard ? (
            <div className="h-[60vh] flex flex-col items-center justify-center text-center" data-testid="canvas-empty">
              <LayoutGrid className="w-12 h-12 text-white/20 mb-4" />
              <h3 className="text-lg font-semibold text-white/80 mb-2">No boards yet</h3>
              <p className="text-sm text-white/40 mb-4">Create your first canvas to start moodboarding</p>
              <Button onClick={() => setNewBoardOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="w-4 h-4 mr-2" />New Board
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold" data-testid="canvas-active-name">{activeBoard.name}</h2>
                  <span className="text-xs text-white/30">{(activeBoard.items || []).length} items</span>
                </div>
                <button onClick={() => deleteBoard(activeBoard.id)} data-testid="canvas-delete-board"
                  className="text-xs text-red-400/70 hover:text-red-400 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" />Delete board
                </button>
              </div>

              <div
                ref={canvasRef}
                onMouseMove={onMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                data-testid="canvas-board-area"
                className="relative w-full min-h-[600px] rounded-xl border border-white/[0.06] bg-[radial-gradient(#1a1a25_1px,transparent_1px)] [background-size:24px_24px] bg-[#0d0d14]/60 overflow-hidden cursor-default"
              >
                {(activeBoard.items || []).length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/30 text-sm pointer-events-none">
                    <MousePointer2 className="w-4 h-4 mr-2" />Add an item to begin
                  </div>
                )}
                {(activeBoard.items || []).map(item => (
                  <div
                    key={item.id}
                    data-testid={`canvas-item-${item.id}`}
                    style={{ left: item.x, top: item.y, width: item.width, height: item.height }}
                    className="absolute group rounded-lg overflow-hidden border border-white/10 bg-[#12121a] shadow-xl"
                  >
                    <div onMouseDown={(e) => startDrag(e, item)}
                      className="h-7 bg-black/60 flex items-center justify-between px-2 cursor-move select-none">
                      <div className="flex items-center gap-1.5 text-[10px] text-white/50 uppercase tracking-wider">
                        <Move className="w-2.5 h-2.5" />{item.type}
                      </div>
                      <button onClick={() => removeItem(item.id)} data-testid={`canvas-item-remove-${item.id}`}
                        className="opacity-0 group-hover:opacity-100 text-red-400/70 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="p-2 h-[calc(100%-1.75rem)] overflow-hidden">
                      {item.type === 'image' && (
                        <img src={item.content} alt={item.label} className="w-full h-full object-cover rounded" draggable={false} />
                      )}
                      {item.type === 'video' && (
                        <video src={item.content} controls className="w-full h-full rounded" />
                      )}
                      {item.type === 'text' && (
                        <div className="text-xs text-white/80 whitespace-pre-wrap overflow-auto">{item.content}</div>
                      )}
                      {item.type === 'note' && (
                        <div className="text-xs text-white/90 whitespace-pre-wrap overflow-auto p-2 rounded h-full" style={{ background: `${item.color}22`, border: `1px solid ${item.color}44` }}>{item.content}</div>
                      )}
                      {item.type === 'link' && (
                        <a href={item.content} target="_blank" rel="noreferrer" className="text-xs text-orange-300 hover:underline break-all">
                          <LinkIcon className="w-3 h-3 inline mr-1" />{item.content}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* New Board Dialog */}
        <Dialog open={newBoardOpen} onOpenChange={setNewBoardOpen}>
          <DialogContent className="bg-[#12121a] border-white/[0.08] text-white" data-testid="canvas-new-board-dialog">
            <DialogHeader><DialogTitle>New Canvas Board</DialogTitle></DialogHeader>
            <Input value={newBoardName} onChange={e => setNewBoardName(e.target.value)} placeholder="Board name"
              data-testid="canvas-new-board-input"
              className="bg-black/40 border-white/10 text-white" />
            <Button onClick={createBoard} data-testid="canvas-create-board-confirm"
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white">Create Board</Button>
          </DialogContent>
        </Dialog>

        {/* Add Item Dialog */}
        <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
          <DialogContent className="bg-[#12121a] border-white/[0.08] text-white" data-testid="canvas-add-item-dialog">
            <DialogHeader><DialogTitle>Add Item to Canvas</DialogTitle></DialogHeader>

            <div className="grid grid-cols-5 gap-1.5">
              {[
                { id: 'image', label: 'Image', icon: ImageIcon },
                { id: 'video', label: 'Video', icon: ImageIcon },
                { id: 'text', label: 'Text', icon: FileText },
                { id: 'note', label: 'Note', icon: Type },
                { id: 'link', label: 'Link', icon: LinkIcon },
              ].map(t => (
                <button key={t.id} onClick={() => setNewItemType(t.id)} data-testid={`canvas-type-${t.id}`}
                  className={`p-2 rounded-md border text-xs flex flex-col items-center gap-1 transition ${newItemType === t.id ? 'border-orange-500/60 bg-orange-500/10 text-orange-300' : 'border-white/10 text-white/60 hover:text-white'}`}>
                  <t.icon className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>

            <Input value={newItemLabel} onChange={e => setNewItemLabel(e.target.value)} placeholder="Label (optional)"
              data-testid="canvas-item-label"
              className="bg-black/40 border-white/10 text-white" />

            {newItemType === 'note' ? (
              <Textarea value={newItemLabel} onChange={e => setNewItemLabel(e.target.value)}
                placeholder="Sticky note text..."
                data-testid="canvas-item-note"
                className="bg-black/40 border-white/10 text-white min-h-[100px]" />
            ) : newItemType === 'text' ? (
              <Textarea value={newItemContent} onChange={e => setNewItemContent(e.target.value)}
                placeholder="Text content..."
                data-testid="canvas-item-text"
                className="bg-black/40 border-white/10 text-white min-h-[100px]" />
            ) : (
              <Input value={newItemContent} onChange={e => setNewItemContent(e.target.value)}
                placeholder={newItemType === 'image' ? 'Image URL (https://...)' : newItemType === 'video' ? 'Video URL' : 'https://...'}
                data-testid="canvas-item-url"
                className="bg-black/40 border-white/10 text-white" />
            )}

            <Button onClick={addItem} data-testid="canvas-add-item-confirm"
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white">Add to Canvas</Button>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
